import { CertificateData } from "@utils/interface/journey.interface";
import { format, isValid, parseISO } from "date-fns";

/* Certificate templates are stored as html with {{token}} placeholders. The
   public site fills the same tokens on the verify-certificates page, so keep
   this map in sync with skillshikshya-website CertificateCard. */
const formatCertificateDate = (date?: string | Date | null) => {
  if (!date) return "";
  const parsed = date instanceof Date ? date : parseISO(date);
  return isValid(parsed) ? format(parsed, "d MMM yyyy") : "";
};

const getStudentName = (certificate: any) =>
  certificate?.student?.full_name ||
  certificate?.student?.user?.full_name ||
  [certificate?.student?.first_name, certificate?.student?.last_name]
    .filter(Boolean)
    .join(" ");

const getReplacements = (certificate: any): Record<string, string> => {
  const student = certificate?.student;
  const courseBatch = certificate?.course_batch;

  return {
    "{{full_name}}": getStudentName(certificate),
    "{{email}}": student?.email || student?.user?.email || "",
    "{{phone}}": student?.phone_number || student?.user?.phone || "",
    /* the certificate carries its own dates in admin, the batch is the fallback */
    "{{enrollment_date}}": formatCertificateDate(
      certificate?.enrollment_date || courseBatch?.start_date
    ),
    "{{completion_date}}": formatCertificateDate(
      certificate?.completion_date || courseBatch?.end_date
    ),
    "{{certificate_id}}": certificate?.certificate_number || "",
    "{{certificate_number}}": certificate?.certificate_number || "",
    "{{remarks}}": certificate?.remarks || "",
    "{{course_batch_name}}": courseBatch?.name || "",
    "{{course_batch_id}}": String(courseBatch?.id ?? ""),
    "{{batch_name}}": courseBatch?.name || "",
    "{{course_name}}": courseBatch?.course?.title || certificate?.course?.title || "",
  };
};

/* returns "" when the certificate has no template to render */
const buildCertificateHtml = (certificate: CertificateData | any): string => {
  const template = certificate?.certificate_template?.html_content;
  if (!template || typeof template !== "string") return "";

  return Object.entries(getReplacements(certificate)).reduce(
    (content, [token, value]) => content.split(token).join(value ?? ""),
    template
  );
};

/* resolve once every image in the print document has settled, so the dialog
   never opens on a half-painted certificate. Capped so a dead image url
   cannot block the print. */
const waitForImages = (doc: Document, timeout = 8000) =>
  new Promise<void>((resolve) => {
    const images = Array.from(doc.images || []);
    if (!images.length) return resolve();

    let settled = 0;
    const timer = window.setTimeout(resolve, timeout);
    const done = () => {
      settled += 1;
      if (settled >= images.length) {
        window.clearTimeout(timer);
        resolve();
      }
    };

    images.forEach((img) => {
      if (img.complete) return done();
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  });

/* webfonts load independently of images — printing before they settle gives a
   fallback-serif first page */
const waitForFonts = (doc: Document, timeout = 5000) =>
  Promise.race([
    (doc as any).fonts?.ready ?? Promise.resolve(),
    new Promise((resolve) => window.setTimeout(resolve, timeout)),
  ]);

const nextFrame = () =>
  new Promise<void>((resolve) =>
    window.requestAnimationFrame(() => resolve())
  );

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap";

const PRINT_STYLES = `
  @page { margin: 0; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    /* templates set no font-family of their own, so without this they fall
       back to the browser default serif (Times New Roman) */
    font-family: "Outfit", "Helvetica Neue", Arial, sans-serif;
    /* templates rely on background colours, which print strips by default */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .certificate-page {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    /* an exact page box, not min-height — otherwise the box can grow past the
       sheet and the certificate rides up */
    height: 100vh;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
  }
  .certificate-page:last-child { page-break-after: auto; break-after: auto; }
  /* auto margins centre a flex item on both axes regardless of what margins
     the template itself carries */
  .certificate-page > * { margin: auto; }
  .certificate-page img { max-width: 100%; }
`;

/* Renders the certificates into an offscreen iframe and opens the browser
   print dialog. One certificate per page. */
const printCertificates = async (
  certificates: (CertificateData | any)[],
  documentTitle = "Certificate"
): Promise<void> => {
  const pages = (certificates || []).map(buildCertificateHtml).filter(Boolean);

  if (!pages.length) {
    throw new Error("No certificate template available to print");
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  /* kept offscreen rather than 0x0 — the page needs real dimensions for the
     centering (min-height: 100vh) to resolve against */
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1200px;height:900px;border:0;";
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  try {
    const doc = iframe.contentWindow?.document;
    if (!doc) throw new Error("Unable to open the print view");

    const markup =
      `<!doctype html><html><head><meta charset="utf-8" />` +
      `<title>${documentTitle}</title>` +
      `<link rel="preconnect" href="https://fonts.googleapis.com" />` +
      `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />` +
      `<link rel="stylesheet" href="${FONT_HREF}" />` +
      `<style>${PRINT_STYLES}</style></head><body>` +
      pages.map((page) => `<div class="certificate-page">${page}</div>`).join("") +
      `</body></html>`;

    /* wait for the document itself before touching its resources — printing
       straight after write() gives a blank first sheet, because nothing has
       been parsed or laid out yet */
    await new Promise<void>((resolve) => {
      iframe.addEventListener("load", () => resolve(), { once: true });
      window.setTimeout(resolve, 3000);
      doc.open();
      doc.write(markup);
      doc.close();
    });

    await waitForFonts(doc);
    await waitForImages(doc);
    /* two frames so the loaded fonts and images are actually painted */
    await nextFrame();
    await nextFrame();

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    /* print() blocks until the dialog closes in most browsers, but tearing the
       iframe down immediately cancels the job in the ones where it does not */
    window.setTimeout(cleanup, 1000);
  } catch (error) {
    cleanup();
    throw error;
  }
};

export { buildCertificateHtml, printCertificates };
